import {
    itemCountSingularScreenReaderOnlyText,
    itemCountPluralScreenReaderOnlyText,
    paused,
} from './FolderNode.locstring.json';
import { unreadCountScreenReaderOnlyText } from 'owa-locstrings/lib/strings/unreadcountscreenreaderonlytext.locstring.json';
import loc from 'owa-localize';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { shallowCompare } from 'office-ui-fabric-react/lib/Utilities';
import { selectFolder } from 'owa-mail-folder-forest-actions';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import isArchiveFolder from 'owa-mail-folder-store/lib/utils/isArchiveFolder';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import folderStore, { Folder } from 'owa-folders';
import publicFolderFavoriteStore from 'owa-public-folder-favorite/lib/store/publicFolderFavoriteStore';
import DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';

import TreeNode, { ChevronProps } from 'owa-tree-node/lib/components/TreeNode';
import * as React from 'react';
import { default as viewStateStore } from 'owa-mail-folder-store/lib/store/store';
import {
    store as bulkActionStore,
    isBulkActionInState,
    BulkActionStateEnum,
    isBulkActionValid,
} from 'owa-bulk-action-store';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import { BulkActionScenarioType } from 'owa-mail-bulk-action-view';
import getBulkActionTooltipText from '../selectors/getBulkActionTooltipText';
import { FolderForestTreeType, FolderForestNodeType } from 'owa-favorites-types';
import { isFolderPaused } from 'owa-mail-list-store';
import { touchHandler, ITouchHandlerParams } from 'owa-mail-touch-handler';

const mailFolderNodeStyles = require('./MailFolderNode.scss');
const classNames = require('classnames/bind').bind(mailFolderNodeStyles);

// Folders that should show total count instead of unread count
const FOLDERS_TO_SHOW_TOTALCOUNT: DistinguishedFolderIdName[] = [
    'drafts',
    'junkemail',
    'outbox',
    'notes',
    'scheduled',
];

export interface FolderNodeProps extends React.HTMLProps<HTMLDivElement> {
    displayName: string;
    folderId: string;
    treeType: FolderForestTreeType;

    // Optional parameters
    chevronProps?: ChevronProps;
    customIcon?: string;
    depth?: number; // root is 0, every sub node increase this number by 1.
    isBeingDragged?: boolean;
    isDroppedOver?: boolean;
    onContextMenu?: (evt: React.MouseEvent<unknown>, folder?: Folder) => void;
    renderCustomTreeNodeDisplay?: (textClassName: string) => JSX.Element; // Method to render the main content (if not specified the tree node will render the default UI)
    showHoverStateOnDroppedOver?: boolean;
    shouldHideToggleFavorite?: boolean;
    isPublicFolder?: boolean;
}

@observer
export default class FolderNode extends React.Component<FolderNodeProps, {}> {
    @computed
    get isSelected(): boolean {
        const selectedNode = getSelectedNode();
        return (
            this.props.treeType == selectedNode.treeType && this.props.folderId == selectedNode.id
        );
    }

    @computed
    get isWithContextMenuOpen(): boolean {
        return this.props.folderId == viewStateStore.withContextMenuFolderId;
    }

    shouldComponentUpdate(nextProps: FolderNodeProps, nextState: {}) {
        return !shallowCompare(this.props, nextProps);
    }

    @computed
    get touchHandler() {
        const touchHandlerParams: ITouchHandlerParams = {
            onLongPress: this.props.onContextMenu,
        };
        return touchHandler(touchHandlerParams);
    }

    render() {
        return (
            <TreeNode
                {...this.touchHandler}
                chevronProps={this.props.chevronProps}
                customIcon={this.props.customIcon}
                depth={this.props.depth}
                displayName={this.props.displayName}
                isBeingDragged={this.props.isBeingDragged}
                isDroppedOver={this.props.isDroppedOver}
                isRootNode={false}
                isSelected={this.isSelected}
                isWithContextMenuOpen={this.isWithContextMenuOpen}
                key={this.props.folderId}
                onKeyDown={this.onKeyDown}
                onClick={this.onClick}
                onContextMenu={this.props.onContextMenu}
                renderCustomTreeNodeDisplay={this.props.renderCustomTreeNodeDisplay}
                renderRightCharm={this.renderRightCharm}
                showAsHoverOnDroppedOver={this.props.showHoverStateOnDroppedOver}
            />
        );
    }

    private shouldRenderBulkActionSpinner(folderId: string): boolean {
        return (
            (isBulkActionInState(folderId, BulkActionStateEnum.Uninitialized) ||
                isBulkActionInState(folderId, BulkActionStateEnum.Running)) &&
            isBulkActionValid(folderId)
        );
    }

    private renderRightCharm = (): JSX.Element => {
        const { folderId } = this.props;

        if (isFolderPaused(folderId)) {
            return this.renderPausedIndicator();
        }

        if (this.shouldRenderBulkActionSpinner(folderId)) {
            return this.renderBulkActionSpinner();
        }

        if (!isArchiveFolder(folderId)) {
            return this.renderUnreadOrTotalCount();
        }

        return null;
    };

    private renderPausedIndicator(): JSX.Element {
        return <>{loc(paused)}</>;
    }

    private renderBulkActionSpinner = (): JSX.Element => {
        const { folderId } = this.props;
        const bulkActionInfo = bulkActionStore.bulkActionInformationMap.get(folderId);

        let tooltipText = '';

        const scenario: BulkActionScenarioType = bulkActionInfo.customData
            ? JSON.parse(bulkActionInfo.customData).Scenario
            : bulkActionInfo.class;
        tooltipText = getBulkActionTooltipText(scenario, folderId);

        return (
            <TooltipHost content={tooltipText} calloutProps={{ gapSpace: 0 }}>
                <Spinner size={SpinnerSize.small} title="" />
            </TooltipHost>
        );
    };

    private renderUnreadOrTotalCount = (): JSX.Element => {
        const { folderId } = this.props;
        const folder = this.getFolder();
        const shouldDisplayTotalCount =
            FOLDERS_TO_SHOW_TOTALCOUNT.indexOf(folderIdToName(folderId)) >= 0 &&
            folder.TotalCount > 0;
        let count = shouldDisplayTotalCount ? folder.TotalCount : folder.UnreadCount;
        count = count == 0 ? null : count; // We do not display the number 0;
        const unreadCountClassNames = classNames(
            mailFolderNodeStyles.unreadOrTotalCount,
            !shouldDisplayTotalCount && mailFolderNodeStyles.unreadCount
        );

        return (
            count && (
                <span className={unreadCountClassNames}>
                    {count}
                    <span className="screenReaderOnly">
                        {// for a treeitem, inner content that have aria-hidden=true are not hidden in IE
                        // therefore we cannot hide the visual string and use a screenReaderOnly string that has the count
                        shouldDisplayTotalCount
                            ? count == 1
                                ? loc(itemCountSingularScreenReaderOnlyText)
                                : loc(itemCountPluralScreenReaderOnlyText)
                            : loc(unreadCountScreenReaderOnlyText)}
                    </span>
                </span>
            )
        );
    };

    private getFolder = (): Folder => {
        const { folderId } = this.props;
        if (this.props.isPublicFolder) {
            return publicFolderFavoriteStore.folderTable.get(folderId);
        }

        return folderStore.folderTable.get(folderId);
    };

    private onClick = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        selectFolder(
            this.props.folderId,
            this.props.treeType,
            'FolderNodeClick' /* actionSource */,
            this.props.isPublicFolder
                ? FolderForestNodeType.PublicFolder
                : FolderForestNodeType.Folder
        );
    };

    private onKeyDown = (evt: React.KeyboardEvent<unknown>) => {
        switch (evt.keyCode) {
            case KeyboardCharCodes.Enter:
            case KeyboardCharCodes.Space:
                evt.stopPropagation();

                // explicity cancelling event so click won't fire after this in case when folder pane is opened as overlay.
                // only stopPropagation is not enough
                evt.preventDefault();
                selectFolder(
                    this.props.folderId,
                    this.props.treeType,
                    'Keyboard' /* actionSource */
                );
                break;
        }
    };
}
